import { useMemo } from 'react';
import {
    useNavigate as useRouterNavigate,
    useLocation,
    useSearchParams as useRouterSearchParams,
    useParams as useRouterParams,
    redirect as routerRedirect,
} from 'react-router-dom';

/**
 * Navigation hook - Abstraction for navigation
 *
 * React Router: Uses useNavigate from react-router-dom
 */
export function useNavigate() {
    const navigate = useRouterNavigate();

    return useMemo(
        () => ({
            push: (path: string) => navigate(path),
            replace: (path: string) => navigate(path, { replace: true }),
            back: () => navigate(-1),
            forward: () => navigate(1),
            refresh: () => window.location.reload(),
        }),
        [navigate],
    );
}

/**
 * Pathname hook - Get current pathname
 *
 * React Router: Uses useLocation().pathname
 */
export function useCurrentPath(): string {
    const location = useLocation();
    return location.pathname;
}

/**
 * Search params hook - Get URL search parameters
 *
 * React Router: Uses useSearchParams from react-router-dom
 */
export function useQueryParams(): URLSearchParams {
    const [searchParams] = useRouterSearchParams();
    return searchParams;
}

/**
 * Params hook - Get route parameters
 *
 * React Router: Uses useParams from react-router-dom
 */
export function useParams<T extends Record<string, string>>(): T {
    return useRouterParams() as T;
}

/**
 * Redirect utility - Client-side redirects
 *
 * React Router: Uses redirect from react-router-dom
 */
export function redirect(path: string): never {
    throw routerRedirect(path);
}

/**
 * Type definitions for router
 */
export type NavigateFunction = ReturnType<typeof useNavigate>;
